import type { MaybeRef } from 'vue';
import type { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import { prepareJsonLdForSerialize } from './jsonLdGraph';

export { dedupeJsonLdGraph, prepareJsonLdForSerialize } from './jsonLdGraph';

/** Page data from useSanityPageQuery — must have page with seoSocial */
export type PageSeoData = MaybeRef<{
  page?: { seoSocial?: { metaTitle?: string; metaDescription?: string } };
} | null>;

export function resolveSiteBaseUrl(domain?: string | null, fallback = ''): string {
  const value = String(domain || '').trim();
  if (!value) {
    return fallback;
  }
  return value.startsWith('http') ? value : `https://${value}`;
}

/**
 * Match a CMS slug against a locale-prefixed path.
 * e.g. `/about-us`, `/en-gb/about-us`; with `allowNested`, also `/en-gb/about-us/team`.
 */
export function matchPathSegment(
  path: string,
  slug: string,
  options?: { allowNested?: boolean },
): boolean {
  const normalized = path.replace(/\/$/, '') || '/';
  if (normalized === `/${slug}` || normalized.endsWith(`/${slug}`)) {
    return true;
  }
  if (options?.allowNested && normalized.includes(`/${slug}/`)) {
    return true;
  }
  return false;
}

export function seoFromPageData(
  pageData: PageSeoData,
  defaultTitle: string,
): { name: string; description: string } {
  const page = unref(pageData)?.page;
  return {
    name: page?.seoSocial?.metaTitle ?? defaultTitle,
    description: page?.seoSocial?.metaDescription ?? '',
  };
}

/** Strip trailing slash; empty becomes `/`. */
export function normalizeRoutePath(path: string): string {
  return path.replace(/\/$/, '') || '/';
}

/**
 * Absolute page URL from site base + route path.
 * Returns null for the homepage path `/`.
 */
export function absolutePageUrlFromPath(baseUrl: string, routePath: string): string | null {
  const path = normalizeRoutePath(routePath);
  if (path === '/') {
    return null;
  }
  return `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Prefer SEO meta title, then document title. */
export function resolveSeoTitle(content: {
  seoSocial?: { metaTitle?: string } | null;
  title?: string | null;
}): string {
  return (content.seoSocial?.metaTitle || content.title || '').trim();
}

export function injectJsonLdScript(key: string, data: MaybeRef<unknown | null>) {
  useHead({
    script: computed(() => {
      const ld = unref(data);
      if (!ld) {
        return [];
      }

      return [
        {
          key,
          type: 'application/ld+json',
          innerHTML: JSON.stringify(prepareJsonLdForSerialize(ld)),
        },
      ];
    }),
  });
}

/** Omit `item` when the crumb is label-only (no real page URL). */
export interface BreadcrumbCrumb {
  name: string;
  item?: string;
}

export function buildBreadcrumbList(id: string, crumbs: BreadcrumbCrumb[]) {
  return {
    '@type': 'BreadcrumbList' as const,
    '@id': id,
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem' as const,
      position: index + 1,
      name: crumb.name,
      ...(crumb.item && { item: crumb.item }),
    })),
  };
}

/**
 * Shared Nuxt/i18n context for JSON-LD builders.
 */
export function useJsonLdShared() {
  const runtimeConfig = useRuntimeConfig();
  const route = useRoute();
  const { locale } = useI18n();
  const localePath = useLocalePath();

  const baseUrl = computed(() =>
    resolveSiteBaseUrl(runtimeConfig.public.domain as string | undefined),
  );

  const organizationId = computed(() => `${baseUrl.value}/#organization`);

  const localeValue = computed(() => unref(locale) as LOCALE_CODE);

  return {
    runtimeConfig,
    route,
    locale,
    localeValue,
    localePath,
    baseUrl,
    organizationId,
    seoFromPageData,
    matchPathSegment,
    injectJsonLdScript,
    resolveSiteBaseUrl,
  };
}
